import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { getAds, createOrder } from '../../services/p2pService';
import { useToast } from '../../context/ToastContext';

export default function OrderPlacement() {
    const { adId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();

    const [ad, setAd] = useState<any>(null);
    const [selectedPm, setSelectedPm] = useState<any>(null);
    const [tab, setTab] = useState<'fiat' | 'crypto'>('fiat');
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingAd, setLoadingAd] = useState(true);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await getAds({ page: '1', limit: '100' });
                const found = res.ads?.find((a: any) => a._id === adId);
                setAd(found ?? null);

                // Payment method here means "which of the SELLER's registered accounts
                // will the buyer send fiat to" — it must come from the ad's own
                // paymentMethods (the ad owner's accounts), never the current viewer's
                // own saved methods. Only the buyer needs to make this choice.
                if (found?.side === 'sell') {
                    const methods = found.paymentMethods ?? [];
                    // Returning from the picker screen with a choice already made?
                    const pick = sessionStorage.getItem('p2p_pm_selected');
                    if (pick) {
                        try {
                            const saved = JSON.parse(pick);
                            const match = methods.find((m: any) => m._id === saved._id);
                            setSelectedPm(match ?? saved);
                            sessionStorage.removeItem('p2p_pm_selected');
                        } catch {}
                    } else if (methods.length === 1) {
                        // Only one option — no need to make the buyer pick.
                        setSelectedPm(methods[0]);
                    }
                }
            } catch { /* ignore */ }
            setLoadingAd(false);
        })();
    }, [adId, location.key]);

    if (loadingAd) return (
        <div style={{ background:'var(--p2p-bg-primary)', minHeight:'100vh', padding:16 }}>
            {/* Header skeleton */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
                <div className="p2p-skel" style={{ width:20, height:20, borderRadius:4 }} />
                <div className="p2p-skel" style={{ width:120, height:18, borderRadius:4 }} />
            </div>
            {/* Tabs skeleton */}
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
                <div className="p2p-skel" style={{ flex:1, height:38, borderRadius:8 }} />
                <div className="p2p-skel" style={{ flex:1, height:38, borderRadius:8 }} />
            </div>
            {/* Amount input skeleton */}
            <div className="p2p-skel" style={{ width:'100%', height:56, borderRadius:8, marginBottom:8 }} />
            <div className="p2p-skel" style={{ width:'60%', height:13, borderRadius:4, marginBottom:20 }} />
            {/* Payment method card skeleton */}
            <div style={{ padding:14, borderRadius:10, border:'1px solid var(--p2p-border-default)', marginBottom:16 }}>
                <div className="p2p-skel" style={{ width:'40%', height:11, borderRadius:4, marginBottom:10 }} />
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div className="p2p-skel" style={{ width:10, height:10, borderRadius:'50%' }} />
                    <div className="p2p-skel" style={{ width:'55%', height:14, borderRadius:4 }} />
                </div>
            </div>
            {/* Seller card skeleton */}
            <div style={{ padding:14, borderRadius:10, border:'1px solid var(--p2p-border-default)', marginBottom:16 }}>
                <div className="p2p-skel" style={{ width:'30%', height:11, borderRadius:4, marginBottom:12 }} />
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div className="p2p-skel" style={{ width:36, height:36, borderRadius:'50%' }} />
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        <div className="p2p-skel" style={{ width:100, height:14, borderRadius:4 }} />
                        <div className="p2p-skel" style={{ width:140, height:11, borderRadius:4 }} />
                    </div>
                </div>
            </div>
            {/* Button skeleton */}
            <div className="p2p-skel" style={{ width:'100%', height:52, borderRadius:8 }} />
        </div>
    );
    if (!ad) return <div className="p2p-empty"><p className="p2p-empty-text">Ad not found</p></div>;

    const price = ad.currentPrice ?? ad.fixedPrice ?? 0;
    const numVal = parseFloat(value || '0');
    const fiatVal = tab === 'fiat' ? numVal : numVal * price;
    const cryptoVal = tab === 'fiat' ? (price > 0 ? numVal / price : 0) : numVal;

    /**
     * Role logic:
     * ad.side === 'sell' → ad owner is SELLER → visitor is BUYER
     *   - Visitor pays fiat, receives crypto
     *   - Visitor needs a payment method (to send fiat)
     *   - Header: "Buy USDT"
     *
     * ad.side === 'buy' → ad owner is BUYER → visitor is SELLER
     *   - Visitor sends crypto, receives fiat
     *   - No payment method needed (ad owner will pay visitor in fiat)
     *   - Header: "Sell USDT"
     */
    const iAmBuyer = ad.side === 'sell';  // ad owner sells → I buy
    const iAmSeller = ad.side === 'buy';  // ad owner buys → I sell

    const handleOpenSelectPm = () => {
        navigate('/p2p/select-payment', { state: { methods: ad.paymentMethods ?? [] } });
        sessionStorage.setItem('p2p_pm_pick', 'waiting');
    };

    const handlePlace = async () => {
        // Buyer must select payment method to send fiat; seller doesn't need one
        if (iAmBuyer && !selectedPm) { toast('Select a payment method to send fiat', 'error'); return; }
        if (!value || fiatVal <= 0) { toast('Enter an amount', 'error'); return; }
        if (fiatVal < ad.minOrderAmount) { toast(`Minimum order: ${ad.minOrderAmount.toLocaleString()} ${ad.fiat}`, 'error'); return; }
        if (fiatVal > ad.maxOrderAmount) { toast(`Maximum order: ${ad.maxOrderAmount.toLocaleString()} ${ad.fiat}`, 'error'); return; }

        setLoading(true);
        try {
            const order = await createOrder({
                adId: ad._id,
                fiatAmount: fiatVal,
                paymentMethodId: iAmBuyer ? selectedPm._id : null,
            });
            toast('Order created!', 'success');
            navigate(`/p2p/order/${order._id}`);
        } catch (e: any) { toast(e.message, 'error'); }
        setLoading(false);
    };

    return (
        <div className="p2p-page">
            <div className="p2p-header">
                <button className="p2p-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} />{iAmBuyer ? 'Buy' : 'Sell'} {ad.crypto}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: iAmBuyer ? 'var(--p2p-buy)' : 'var(--p2p-sell)', fontVariantNumeric: 'tabular-nums' }}>
                        {price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                    {ad.priceType === 'floating' && <span className="p2p-badge-fluctuating">Fluctuating</span>}
                </div>
            </div>

            <div className="p2p-page-content" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Input Tabs */}
                <div className="p2p-tabs">
                    <button className={`p2p-tab ${tab === 'fiat' ? 'active' : ''}`} onClick={() => setTab('fiat')}>By {ad.fiat}</button>
                    <button className={`p2p-tab ${tab === 'crypto' ? 'active' : ''}`} onClick={() => setTab('crypto')}>By {ad.crypto}</button>
                </div>

                {/* Amount Input */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                        <input
                            className="p2p-hero-input"
                            style={{ flex: 1 }}
                            placeholder="0.00"
                            value={value}
                            onChange={e => setValue(e.target.value.replace(/[^0-9.]/g, ''))}
                        />
                        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--p2p-text-muted)', paddingBottom: 8 }}>
                            {tab === 'fiat' ? ad.fiat : ad.crypto}
                        </span>
                    </div>

                    {/* Role-aware receive/send labels */}
                    {iAmBuyer ? (
                        // I'm buyer: I pay fiat → I receive crypto
                        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--p2p-text-muted)' }}>
                            You pay: <strong style={{ color: 'var(--p2p-text-primary)' }}>
                                {tab === 'fiat'
                                    ? `${numVal.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${ad.fiat}`
                                    : `${fiatVal.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${ad.fiat}`}
                            </strong>
                            {'  ·  '}
                            You receive: <strong style={{ color: 'var(--p2p-buy)' }}>
                                {tab === 'fiat'
                                    ? `${parseFloat(cryptoVal.toFixed(8)).toString()} ${ad.crypto}`
                                    : `${parseFloat(numVal.toFixed(8)).toString()} ${ad.crypto}`}
                            </strong>
                        </div>
                    ) : (
                        // I'm seller: I send crypto → I receive fiat
                        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--p2p-text-muted)' }}>
                            You send: <strong style={{ color: 'var(--p2p-sell)' }}>
                                {tab === 'fiat'
                                    ? `${parseFloat(cryptoVal.toFixed(8)).toString()} ${ad.crypto}`
                                    : `${parseFloat(numVal.toFixed(8)).toString()} ${ad.crypto}`}
                            </strong>
                            {'  ·  '}
                            You receive: <strong style={{ color: 'var(--p2p-text-primary)' }}>
                                {tab === 'fiat'
                                    ? `${numVal.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${ad.fiat}`
                                    : `${fiatVal.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${ad.fiat}`}
                            </strong>
                        </div>
                    )}

                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--p2p-text-muted)' }}>
                        Limit: {ad.minOrderAmount?.toLocaleString()} – {ad.maxOrderAmount?.toLocaleString()} {ad.fiat}
                        {tab === 'crypto' && price > 0 && (
                            <span style={{ color: 'var(--p2p-text-disabled)', marginLeft: 6 }}>
                                (~{parseFloat((ad.minOrderAmount / price).toFixed(8)).toString()} – {parseFloat((ad.maxOrderAmount / price).toFixed(8)).toString()} {ad.crypto})
                            </span>
                        )}
                    </div>
                    {/* Real-time limit validation */}
                    {fiatVal > 0 && fiatVal < ad.minOrderAmount && (
                        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--p2p-sell)', fontWeight: 500 }}>
                            ⚠ Below minimum — equivalent fiat is {fiatVal.toLocaleString(undefined, { maximumFractionDigits: 2 })} {ad.fiat}, minimum is {ad.minOrderAmount.toLocaleString()} {ad.fiat}
                        </div>
                    )}
                    {fiatVal > 0 && fiatVal > ad.maxOrderAmount && (
                        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--p2p-sell)', fontWeight: 500 }}>
                            ⚠ Exceeds maximum — equivalent fiat is {fiatVal.toLocaleString(undefined, { maximumFractionDigits: 2 })} {ad.fiat}, maximum is {ad.maxOrderAmount.toLocaleString()} {ad.fiat}
                        </div>
                    )}
                </div>

                {/* Payment method — which of the SELLER's accounts the buyer will pay into */}
                {iAmBuyer && (
                    <div className="p2p-card" style={{ cursor: 'pointer' }} onClick={handleOpenSelectPm}>
                        <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)', marginBottom: 8 }}>Pay Seller Via</div>
                        <div style={{ fontSize: 11, color: 'var(--p2p-text-disabled)', marginBottom: 8 }}>
                            Select how you will send {ad.fiat} to the seller
                        </div>
                        {selectedPm ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span className="p2p-pm-dot" style={{ background: selectedPm.methodColor, width: 10, height: 10 }} />
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                            <span style={{ fontSize: 14, color: 'var(--p2p-text-primary)', fontWeight: 600 }}>
                                                {selectedPm.extraFields?.bankName || selectedPm.bankName || selectedPm.methodType}
                                            </span>
                                            {(selectedPm.extraFields?.bankName || selectedPm.bankName) && (
                                                <span style={{ fontSize: 11, fontWeight: 500, background: 'var(--p2p-bg-elevated)', border: '1px solid var(--p2p-border-input)', color: 'var(--p2p-text-muted)', borderRadius: 4, padding: '1px 6px' }}>
                                                    {selectedPm.methodType}
                                                </span>
                                            )}
                                        </div>
                                        <span style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>
                                            {selectedPm.accountNumber
                                                ? `****${String(selectedPm.accountNumber).slice(-4)}`
                                                : selectedPm.phoneNumber
                                                ? `${String(selectedPm.phoneNumber).slice(0,4)}****`
                                                : selectedPm.accountName}
                                        </span>
                                    </div>
                                </div>
                                <ChevronRight size={16} color="var(--p2p-text-muted)" />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ color: (ad.paymentMethods ?? []).length === 0 ? 'var(--p2p-sell)' : 'var(--p2p-text-muted)', fontSize: 14 }}>
                                    {(ad.paymentMethods ?? []).length === 0 ? '⚠ Seller has no payment methods on this ad' : 'Select payment method'}
                                </span>
                                <ChevronRight size={16} color="var(--p2p-text-muted)" />
                            </div>
                        )}
                    </div>
                )}

                {/* For seller: show accepted payment types from the ad (how ad owner will pay them) */}
                {iAmSeller && (
                    <div className="p2p-card">
                        <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)', marginBottom: 8 }}>You will be paid via</div>
                        <div style={{ fontSize: 11, color: 'var(--p2p-text-disabled)', marginBottom: 8 }}>
                            The buyer will send {ad.fiat} to you using one of these methods
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {(ad.paymentMethods ?? []).map((m: any, i: number) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span className="p2p-pm-dot" style={{ background: m.methodColor ?? '#2563eb', width: 10, height: 10 }} />
                                    <div>
                                        <span style={{ fontSize: 14, color: 'var(--p2p-text-primary)' }}>{m.methodType}</span>
                                        {m.extraFields?.bankName && <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>{m.extraFields.bankName}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Advertiser Info — tappable so the buyer can review the seller's profile
                    (stats, verification, feedback) before placing the order */}
                <div className="p2p-card">
                    <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)', marginBottom: 8 }}>
                        {iAmBuyer ? 'Seller' : 'Buyer'}
                    </div>
                    <div
                        onClick={() => ad.user?._id && navigate(`/p2p/profile/${ad.user._id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: ad.user?._id ? 'pointer' : 'default' }}
                    >
                        <div className="p2p-avatar">{(ad.user?.nickname ?? ad.user?.name ?? '?').slice(0, 1).toUpperCase()}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--p2p-text-primary)' }}>{ad.user?.nickname ?? ad.user?.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>{ad.paymentTimeLimit ?? 15} min payment window</div>
                        </div>
                        {ad.user?._id && <ChevronRight size={18} color="var(--p2p-text-muted)" />}
                    </div>
                    {ad.terms && (
                        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--p2p-text-secondary)', lineHeight: 1.6, borderTop: '1px solid var(--p2p-border-default)', paddingTop: 12, whiteSpace: 'pre-wrap' }}>
                            {ad.terms}
                        </div>
                    )}
                    {/* Terms acknowledgement — required before placing order */}
                    {ad.terms && (
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, cursor: 'pointer', paddingTop: 12, borderTop: '1px solid var(--p2p-border-default)' }}>
                            <input
                                type="checkbox"
                                checked={agreedToTerms}
                                onChange={e => setAgreedToTerms(e.target.checked)}
                                style={{ accentColor: 'var(--p2p-buy)', width: 16, height: 16, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: 13, color: 'var(--p2p-text-secondary)', lineHeight: 1.5 }}>
                                I have read and agree to the {iAmBuyer ? 'seller' : 'buyer'}'s trade terms above
                            </span>
                        </label>
                    )}
                </div>

                {/* Escrow notice */}
                <div className="p2p-info-bar">
                    <AlertCircle size={16} style={{ flexShrink: 0 }} />
                    {iAmBuyer
                        ? <span>Once you place the order, seller's {ad.crypto} will be held in escrow by Bicoin until you confirm payment.</span>
                        : <span>Once you place the order, your {ad.crypto} will be held in escrow by Bicoin. Release it after receiving {ad.fiat} payment.</span>
                    }
                </div>

                <button
                    className={iAmBuyer ? 'p2p-btn-yellow' : 'p2p-btn-sell'}
                    style={{ height: 52, borderRadius: 8, fontSize: 16, fontWeight: 700, border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}
                    onClick={handlePlace}
                    disabled={loading || (iAmBuyer && !selectedPm) || (fiatVal > 0 && (fiatVal < ad.minOrderAmount || fiatVal > ad.maxOrderAmount)) || (!!ad.terms && !agreedToTerms)}
                >
                    {loading ? 'Placing order...' : iAmBuyer ? `Buy ${ad.crypto}` : `Sell ${ad.crypto}`}
                </button>
            </div>
        </div>
    );
}
